import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import type {
	ChildrenQueryData,
	SetIssueExpanded,
} from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { createItemSkeleton, createRowForInlineCreate } from '../../common/utils/row-placeholder';
import { DEFAULT_CACHE_VARIABLES } from '../../constants';
import type { GroupItemsQueryData } from '../../services/get-item-groups/types';
import type {
	ListViewIssue,
	ListViewIssuesReturn,
} from '../../services/get-list-view-issues/types';
import type { RowCreatePosition, RowCreateState } from '../row-create';
import type { ListViewControllerData } from './types';

export const convertToListViewControllerData = ({
	data,
	childrenQueryData,
	rowCreateState,
	setRowCreatePosition,
	setIssueExpanded,
	groups,
	groupItemsQueryData,
}: {
	data: ListViewIssue[];
	groups?: ListViewIssue[];
	childrenQueryData?: ChildrenQueryData<ListViewIssuesReturn>;
	groupItemsQueryData?: GroupItemsQueryData;
	setIssueExpanded?: SetIssueExpanded;
	rowCreateState: RowCreateState;
	setRowCreatePosition: (rowCreatePosition: RowCreatePosition) => void;
}): {
	convertedData: ListViewControllerData[];
	issuesMap: Map<string, ListViewControllerData>;
} => {
	const issuesMap = new Map<string, ListViewControllerData>();

	const addAndReturnIssue = (issue: ListViewControllerData) => {
		issuesMap.set(String(issue.issueId), issue);
		return issue;
	};

	const convertListViewIssueToListViewControllerData = ({
		issue,
		children,
		issueHasChildCreateOpen,
		canCreateChild,
		rankIndex,
		parentIssueId,
	}: {
		issue: ListViewIssue;
		children: ListViewControllerData[] | undefined;
		issueHasChildCreateOpen: boolean;
		canCreateChild: boolean;
		rankIndex: number;
		parentIssueId?: string;
	}): ListViewControllerData => {
		if (isGroupItem(issue.issueId) && groupItemsQueryData) {
			return addAndReturnIssue({
				...issue,
				rankIndex,
				children: issueHasChildCreateOpen
					? [createRowForInlineCreate('group'), ...(children || [])]
					: children,
			});
		}

		if (childrenQueryData) {
			return addAndReturnIssue({
				...issue,
				rankIndex,
				parentIssueId,
				children: issueHasChildCreateOpen
					? [...(children || []), createRowForInlineCreate('subtask')]
					: children,
				onChildCreateButtonClicked: canCreateChild
					? () => {
							setRowCreatePosition({
								parentId: issue.issueId,
							});
							if (setIssueExpanded) {
								setIssueExpanded(toIssueId(issue.issueId), true);
							}
						}
					: undefined,
			});
		}

		return addAndReturnIssue({
			...issue,
			rankIndex,
		});
	};

	const isGrouping = typeof groups !== 'undefined' ? groups.length > 0 : false;
	if ((isGrouping && !groupItemsQueryData) || !childrenQueryData) {
		return {
			convertedData: data.map((issue, index) =>
				convertListViewIssueToListViewControllerData({
					issue,
					children: undefined,
					issueHasChildCreateOpen: false,
					canCreateChild: false,
					rankIndex: index,
					parentIssueId: undefined,
				}),
			),
			issuesMap,
		};
	}

	const items = isGrouping ? groups : data;

	const convertedData = (items || []).map((issue, index) => {
		// TODO https://jbusiness.atlassian.net/browse/JET-1723 need to convert to string
		const { issueId: issueIdNumber, childSummary } = issue;
		const issueId = String(issueIdNumber);
		const { parentId } = rowCreateState;
		const issueHasChildCreateOpen = parentId === issue.issueId;
		const hasNoChildren = !childSummary?.hasChildren;

		if (hasNoChildren) {
			return convertListViewIssueToListViewControllerData({
				issue,
				children: undefined,
				issueHasChildCreateOpen,
				canCreateChild: true,
				parentIssueId: undefined,
				rankIndex: index,
			});
		}

		// Getting children from either GroupQueryData or ChildrenQueryData and merging them with items
		let children: ListViewControllerData[];

		const childIssuesData = childrenQueryData ? childrenQueryData[issue.issueId] : undefined;
		const groupItemsData = groupItemsQueryData ? groupItemsQueryData[issue.issueId] : undefined;
		const childItemsData = isGrouping ? groupItemsData : childIssuesData;

		const loading = isGrouping
			? !groupItemsData || groupItemsData?.loading
			: !childIssuesData || childIssuesData?.loading;

		if (loading === true) {
			children = [createItemSkeleton({ issueId })];
		} else {
			children = childItemsData
				? childItemsData.data.map((childIssue, childIndex) => {
						const convertedChildIssue = convertListViewIssueToListViewControllerData({
							issue: childIssue,
							// TODO remove undefined children for recursive call once we introduce multiple hierarchy levels
							children: undefined,
							issueHasChildCreateOpen: false,
							// children items cannot create child anymore, TODO when multiple hierarchy introduced, this logic will need to change
							canCreateChild: false,
							parentIssueId: issueId,
							rankIndex: childIndex,
						});

						return convertedChildIssue;
					})
				: [];
		}

		const convertedIssue = convertListViewIssueToListViewControllerData({
			issue,
			children,
			issueHasChildCreateOpen,
			canCreateChild: true,
			parentIssueId: undefined,
			rankIndex: index,
		});
		return convertedIssue;
	});

	return {
		convertedData,
		issuesMap,
	};
};

const EMTPY_ROW_PREFIX = 'EMPTY_ROW';

export const isEmptyRow = (issueId = '') => issueId.toString().startsWith(EMTPY_ROW_PREFIX);

export const EMPTY_ROW: ListViewControllerData[] = [
	{
		id: '',
		issueId: EMTPY_ROW_PREFIX,
		typeId: '',
		issuetype: {},
		key: '',
		rank: '',
		rankIndex: -1,
		status: {
			categoryId: '',
			name: '',
		},
		totalSubtasks: 0,
		cacheVariables: DEFAULT_CACHE_VARIABLES,
	},
];
